//CouponFormDialog.vue
<template lang="pug">
v-dialog(
  v-model="dialog"
  max-width="800"
  persistent
  scrollable
)
  v-card
    v-card-title.primary--text.headline.pb-2
      | {{ editing ? 'Editar Cupón' : 'Crear Nuevo Cupón' }}
    
    v-card-text.py-0
      v-form(ref="form" v-model="valid")
        //- SECCIÓN: GENERAL
        v-sheet.px-2.pb-1.mt-3.mb-6.rounded-lg.elevation-0
          h3.headline.primary--text.font-weight-medium.mb-4 Información General
        
          v-row.mb-2
            v-col(cols="12" md="6")
              v-text-field(
                v-model="couponData.title"
                label="Título*"
                :rules="[rules.required]"
                required
                dense
                outlined
                hide-details="auto"
              )
            v-col(cols="12" md="6")
              //- El código no se puede editar
              v-text-field(
                v-if="editing"
                v-model="coupon.code"
                label="Código"
                readonly
                dense
                outlined
                hide-details="auto"
                disabled
              )
              v-select(
                v-else
                v-model="couponData.type"
                :items="typeOptions"
                label="Tipo de Cupón*"
                :rules="[rules.required]"
                required
                dense
                outlined
                hide-details="auto"
              )
          
          v-row.mb-2
            v-col(cols="12")
              v-textarea(
                v-model="couponData.description"
                label="Descripción"
                rows="2"
                dense
                outlined
                hide-details="auto"
              )
          
          v-row.mb-2
            v-col(cols="12" md="6")
              v-select(
                v-model="couponData.discountType"
                :items="discountTypeOptions"
                label="Tipo de Descuento*"
                :rules="[rules.required]"
                required
                dense
                outlined
                hide-details="auto"
              )
            v-col(cols="12" md="6")
              v-autocomplete(
                v-if="couponData.discountType === 'FREE_ITEM'"
                v-model="couponData.discountValue"
                :items="products"
                item-text="name"
                item-value="_id"
                label="Producto Gratis*"
                :loading="loadingProducts"
                :disabled="loadingProducts || products.length === 0"
                dense
                outlined
                hide-details="auto"
                clearable
              )
              v-text-field(
                v-else
                v-model.number="couponData.discountValue"
                :label="getDiscountValueLabel"
                type="number"
                :rules="[rules.required, rules.minValue]"
                required
                dense
                outlined
                hide-details="auto"
              )
          
          v-row
            v-col(cols="12" md="6")
              v-text-field(
                v-model.number="couponData.minOrderAmount"
                label="Monto Mínimo de Compra ($)"
                type="number"
                min="0"
                dense
                outlined
                hide-details="auto"
              )
            v-col(cols="12" md="6")
              v-text-field(
                v-model.number="couponData.maxDiscountAmount"
                label="Tope Máximo de Descuento ($)"
                type="number"
                min="0"
                dense
                outlined
                hide-details="auto"
                :hint="couponData.discountType === 'PERCENTAGE' ? 'Recomendado para descuentos porcentuales' : ''"
                persistent-hint
              )
          
        //- SECCIÓN: CONDICIONES TEMPORALES
        v-sheet.px-2.pb-1.mt-8.mb-6.rounded-lg.elevation-0
          h3.headline.primary--text.font-weight-medium.mb-4 Condiciones Temporales
          
          v-row.mb-2
            v-col(cols="12" md="6")
              v-text-field(
                v-model="couponData.startDate"
                label="Fecha de Inicio"
                type="date"
                dense
                outlined
                hide-details="auto"
              )
            v-col(cols="12" md="6")
              v-text-field(
                v-model="couponData.expiresAt"
                label="Fecha de Expiración"
                type="date"
                dense
                outlined
                hide-details="auto"
              )

          v-row.mb-2
            v-col(cols="12" md="6")
              v-menu(
                ref="menuFromTime"
                v-model="fromTimeMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="couponData.fromTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              )
                template(v-slot:activator="{ on, attrs }")
                  v-text-field(
                    v-model="couponData.fromTime"
                    label="Hora Desde"
                    readonly
                    dense
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    hide-details="auto"
                    prepend-inner-icon="mdi-clock-outline"
                  )
                v-time-picker(
                  v-if="fromTimeMenu"
                  v-model="couponData.fromTime"
                  full-width
                  format="24hr"
                  @click:minute="$refs.menuFromTime.save(couponData.fromTime)"
                )

            v-col(cols="12" md="6")
              v-menu(
                ref="menuToTime"
                v-model="toTimeMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="couponData.toTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              )
                template(v-slot:activator="{ on, attrs }")
                  v-text-field(
                    v-model="couponData.toTime"
                    label="Hora Hasta"
                    readonly
                    dense
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    hide-details="auto"
                    prepend-inner-icon="mdi-clock-outline"
                  )
                v-time-picker(
                  v-if="toTimeMenu"
                  v-model="couponData.toTime"
                  full-width
                  format="24hr"
                  @click:minute="$refs.menuToTime.save(couponData.toTime)"
                )
          
          v-row
            v-col(cols="12")
              v-select(
                v-model="couponData.validDays"
                :items="daysOfWeekOptions"
                label="Días Válidos"
                multiple
                chips
                dense
                outlined
                hide-details="auto"
              )  

        //- SECCIÓN: RESTRICCIONES
        v-sheet.px-2.pb-1.mt-8.mb-6.rounded-lg.elevation-0
          h3.headline.primary--text.font-weight-medium.mb-4 Restricciones y Límites
          
          v-row.mb-2
            v-col(cols="12")
              v-select(
                v-model="couponData.validOrderChannels"
                :items="channelOptions"
                label="Canales Válidos"
                multiple
                chips
                dense
                outlined
                hide-details="auto"
                hint="Si no se selecciona ninguno, válido para todos los canales"
                persistent-hint
              )
            
          v-row.mb-2
            v-col(cols="12")
              v-autocomplete(
                v-model="couponData.validCategories"
                :items="categories"
                item-text="name"
                item-value="_id"
                label="Categorías Válidas"
                multiple
                chips
                dense
                outlined
                hide-details="auto"
                hint="Si no se selecciona ninguna, válido para todas las categorías"
                persistent-hint
              )
              
          v-row.mb-2
            v-col(cols="12" md="6")
              v-text-field(
                v-model.number="couponData.maxUses"
                label="Límite Total de Usos"
                type="number"
                min="1"
                dense
                outlined
                hide-details="auto"
                hint="Cantidad máxima de veces que se puede usar este cupón en total"
                persistent-hint
              )
            v-col(cols="12" md="6")
              v-text-field(
                v-model.number="couponData.maxUsesPerCustomer"
                label="Límite de Usos por Cliente"
                type="number"
                min="1"
                dense
                outlined
                hide-details="auto"
                hint="Cantidad máxima de veces que un mismo cliente puede usar este cupón"
                persistent-hint
              )
            
          // Otras restricciones se pueden agregar aquí
          
        //- SECCIÓN: CONFIGURACIÓN AVANZADA
        v-sheet.px-2.pb-1.mt-8.mb-6.rounded-lg.elevation-0
          h3.headline.primary--text.font-weight-medium.mb-4 Configuración Avanzada
          
          v-row.mb-2
            v-col(cols="12" md="6")
              v-switch(
                v-model="couponData.combinable"
                label="Combinable con otras promociones"
                color="primary"
                hide-details="auto"
              )
            v-col(cols="12" md="6")
              v-switch(
                v-model="couponData.active"
                label="Activo"
                color="success"
                hide-details="auto"
              )
          
          v-row.mb-2
            v-col(cols="12" md="6")
              v-select(
                v-model="couponData.generatedBy"
                :items="generatedByOptions"
                label="Generado por"
                dense
                outlined
                hide-details="auto"
              )
            v-col(cols="12" md="6" v-if="couponData.type === 'REFERRAL'")
              v-autocomplete(
                v-model="couponData.relatedEntity.entityId"
                :items="customers"
                :loading="loadingCustomers"
                item-text="displayName"
                item-value="_id"
                label="Cliente Referente"
                placeholder="Buscá un cliente"
                dense
                outlined
                hide-details="auto"
                return-object
                @input="handleCustomerSelected"
                :search-input.sync="customerSearch"
              )
                template(v-slot:item="{ item }")
                  v-list-item-content
                    v-list-item-title {{item.displayName}}
                    v-list-item-subtitle {{item.email}} - {{item.phone}}
            
            v-col(cols="12" md="6" v-if="couponData.type === 'COMPENSATION'")
              .mb-1.caption.grey--text.text--darken-1 Ingresá el número de pedido completo (ej: SALON-00425)
              .d-flex
                v-text-field(
                  v-model="orderNumberInput"
                  label="Número de pedido exacto"
                  placeholder="SALON-00425"
                  dense
                  outlined
                  hide-details="auto"
                  prepend-inner-icon="mdi-pound"
                  :error-messages="orderNumberError"
                  :disabled="orderSelected"
                  class="flex-grow-1"
                )
                v-btn(
                  color="primary"
                  :loading="loadingOrderByNumber"
                  :disabled="!orderNumberInput || orderNumberInput.length < 5 || orderSelected"
                  @click="findOrderByNumber"
                  class="ml-2 mt-1"
                  small
                ) Buscar
                v-btn(
                  v-if="orderSelected"
                  icon
                  small
                  color="error"
                  @click="clearSelectedOrder"
                  class="ml-1 mt-1"
                )
                  v-icon mdi-close
              
              v-alert(
                v-if="selectedOrder"
                dense
                text
                color="success"
                class="mt-3 mb-0"
              )
                .d-flex.justify-space-between.align-center
                  div
                    | Pedido seleccionado: 
                    strong # {{selectedOrder.orderNumber}}
                  div ${{selectedOrder.total}}
    
    v-divider
    
    v-card-actions.py-3
      v-spacer
      v-btn(text @click="close" class="mr-2") Cancelar
      v-btn(
        color="primary"
        :disabled="!valid"
        @click="submit"
      ) Guardar
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'CouponFormDialog',
  
  props: {
    value: Boolean,
    coupon: {
      type: Object,
      default: () => ({})
    },
    editing: Boolean
  },
  
  data() {
    return {
      dialog: this.value,
      valid: false,
      activeTab: 0,
      couponData: {
        title: '',
        description: '',
        type: 'STANDARD',
        discountType: 'PERCENTAGE',
        discountValue: 10,
        minOrderAmount: 0,
        maxDiscountAmount: null,
        validCategories: [],
        startDate: new Date().toISOString().substr(0, 10),
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substr(0, 10),
        validDays: [],
        validHours: {
          start: '',
          end: ''
        },
        maxUses: 100,
        maxUsesPerCustomer: 1,
        customerSegment: 'ALL',
        validChannels: [],
        generatedBy: 'MANUAL',
        combinable: false,
        image: '',
        customerAssigned: null,
        // Inicializar la propiedad relatedEntity para evitar errores cuando se selecciona REFERRAL o COMPENSATION
        relatedEntity: {
          entityId: ''
        },
        active: true
      },
      rules: {
        required: v => !!v || 'Este campo es requerido',
        minValue: v => v > 0 || 'El valor debe ser mayor a cero',
        dateAfterToday: v => {
          const today = new Date().toISOString().substr(0, 10)
          return v >= today || 'La fecha debe ser igual o posterior a hoy'
        }
      },
      typeOptions: [
        { text: 'Estándar', value: 'STANDARD' },
        { text: 'Primera compra', value: 'FIRST_ORDER' },
        { text: 'Fidelidad', value: 'LOYALTY' },
        { text: 'Cumpleaños', value: 'BIRTHDAY' },
        { text: 'Referido', value: 'REFERRAL' },
        { text: 'Recuperación', value: 'RECOVERY' },
        { text: 'Compensación', value: 'COMPENSATION' },
        { text: 'Temporada', value: 'SEASONAL' }
      ],
      discountTypeOptions: [
        { text: 'Porcentaje', value: 'PERCENTAGE' },
        { text: 'Monto Fijo', value: 'FIXED_AMOUNT' },
        { text: 'Envío Gratis', value: 'FREE_SHIPPING' },
        { text: 'Item Gratis', value: 'FREE_ITEM' }
      ],
      dayOptions: [
        { text: 'Domingo', value: 0 },
        { text: 'Lunes', value: 1 },
        { text: 'Martes', value: 2 },
        { text: 'Miércoles', value: 3 },
        { text: 'Jueves', value: 4 },
        { text: 'Viernes', value: 5 },
        { text: 'Sábado', value: 6 }
      ],
      channelOptions: [
        { text: 'Delivery', value: 'DELIVERY' },
        { text: 'Retiro en Local', value: 'PICKUP' },
        { text: 'Consumo en Local', value: 'DINE_IN' }
      ],
      generatedByOptions: [
        { text: 'Manual', value: 'MANUAL' },
        { text: 'Automático', value: 'AUTOMATIC' },
        { text: 'API', value: 'API' }
      ],
      categories: [], // Esto se cargará desde la API
      products: [],   // Esto se cargará desde el store
      customers: [],   // Esto se cargará desde la API
      orders: [],     // Esto se cargará desde la API
      
      // Variables para búsqueda
      customerSearch: '',
      orderNumberInput: '',
      orderNumberError: '',
      selectedOrder: null,
      orderSelected: false,
      loadingCustomers: false,
      loadingProducts: false,
      loadingOrderByNumber: false,
      
      // Menús para selectores de tiempo
      fromTimeMenu: false,
      toTimeMenu: false,
      
      // Opciones para días de la semana
      daysOfWeekOptions: [
        { text: 'Domingo', value: 0 },
        { text: 'Lunes', value: 1 },
        { text: 'Martes', value: 2 },
        { text: 'Miércoles', value: 3 },
        { text: 'Jueves', value: 4 },
        { text: 'Viernes', value: 5 },
        { text: 'Sábado', value: 6 }
      ],
      

      
      // Indica si se está cargando datos
      loading: false
    }
  },
  
  computed: {
    ...mapState({
      currentRestaurant: state => state.restaurant.current
    }),
    getDiscountValueLabel() {
      switch(this.couponData.discountType) {
        case 'PERCENTAGE':
          return 'Porcentaje de Descuento (%)*'
        case 'FIXED_AMOUNT':
          return 'Monto Fijo de Descuento ($)*'
        case 'FREE_SHIPPING':
          return 'Valor del Envío Gratis ($)*'
        case 'FREE_ITEM':
          return 'ID del Producto Gratis*'
        default:
          return 'Valor de Descuento*'
      }
    }
  },
  
  watch: {
    value(val) {
      this.dialog = val
    },
    dialog(val) {
      if (!val) this.$emit('input', val)
    },
    coupon: {
      handler(newCoupon) {
        if (newCoupon) {
          this.initForm(newCoupon)
        }
      },
      immediate: true
    },
    
    // Observar el tipo de cupón para limpiar relatedEntity cuando cambia
    'couponData.type': function(newType) {
      // Si cambia el tipo y no es compensación o referido, limpiar el relatedEntity
      if (newType !== 'COMPENSATION' && newType !== 'REFERRAL') {
        this.couponData.relatedEntity = { entityId: '' };
      } else {
        // Establecer el tipo de entidad según el tipo de cupón
        this.couponData.relatedEntity.entityType = newType === 'REFERRAL' ? 'customer' : 'order';
      }
    },
    
    // Watchers para buscar clientes y pedidos
    customerSearch: function(val) {
      if (val && val.length > 2) {
        this.searchCustomers(val);
      }
    },
    
    orderSearch: function(val) {
      if (val && val.length > 2) {
        this.searchOrders(val);
      }
    }
  },
  

  
  created() {
    this.fetchCategories()
    this.fetchCustomers()
    this.fetchProducts()
  },
  
  methods: {
    ...mapActions({
      fetchCategoriesAction: 'categories/fetch',
      fetchCustomersAction: 'customer/fetchSalon',
      searchCustomersAction: 'customer/search',
      findOrderByNumberAction: 'orders/findByOrderNumber',
      fetchProductsByRestaurantAction: 'products/fetchByRestaurant'
    }),
    
    async fetchCategories() {
      try {
        this.loading = true
        const categories = await this.fetchCategoriesAction()
        this.categories = categories || []
      } catch (error) {
        console.error('Error al cargar categorías:', error)
      } finally {
        this.loading = false
      }
    },
    
    async fetchCustomers() {
      try {
        this.loading = true
        await this.fetchCustomersAction()
        // Obtenemos los clientes del state después de cargarlos
        this.customers = this.$store.state.customer.customers || []
      } catch (error) {
        console.error('Error al cargar clientes:', error)
      } finally {
        this.loading = false
      }
    },
    
    async fetchProducts() {
      try {
        this.loadingProducts = true
        const restaurantId = this.currentRestaurant && this.currentRestaurant._id
        if (restaurantId) {
          await this.fetchProductsByRestaurantAction(restaurantId)
          this.products = this.$store.state.products.list || []
        } else {
          // Fallback: cargar lista general si no hay restaurante en contexto
          try {
            await this.$store.dispatch('products/fetch')
            this.products = this.$store.state.products.list || []
          } catch (_) {
            this.products = this.$store.state.products?.list || []
          }
        }
      } catch (error) {
        console.error('Error al cargar productos:', error)
      } finally {
        this.loadingProducts = false
      }
    },
    
    initForm(coupon) {
      // Hacer una copia profunda para no modificar el original
      this.couponData = JSON.parse(JSON.stringify({
        ...this.couponData,
        ...coupon,
        // Asegurar formato de fechas
        startDate: coupon.startDate ? new Date(coupon.startDate).toISOString().substr(0, 10) : this.couponData.startDate,
        expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt).toISOString().substr(0, 10) : this.couponData.expiresAt,
        // Asegurar que validHours siempre tenga la estructura correcta
        validHours: coupon.validHours || { start: '', end: '' }
      }))
    },
    
    submit() {
      if (this.$refs.form.validate()) {
        // Preparar datos para enviar
        const couponToSave = { ...this.couponData }
        
        // Convertir fechas a objetos Date
        if (couponToSave.startDate) {
          couponToSave.startDate = new Date(couponToSave.startDate)
        }
        couponToSave.expiresAt = new Date(couponToSave.expiresAt)
        
        // Limpiar campos vacíos que deberían ser null
        if (!couponToSave.validHours.start && !couponToSave.validHours.end) {
          delete couponToSave.validHours
        }
        
        if (couponToSave.validCategories.length === 0) {
          delete couponToSave.validCategories
        }
        
        if (couponToSave.validChannels.length === 0) {
          delete couponToSave.validChannels
        }
        
        if (couponToSave.validDays.length === 0) {
          delete couponToSave.validDays
        }
        
        // Manejar campo relatedEntity según el tipo de cupón
        if (couponToSave.type !== 'REFERRAL' && couponToSave.type !== 'COMPENSATION') {
          // Si no es un tipo que requiere relatedEntity, eliminarlo completamente
          delete couponToSave.relatedEntity;
        } else if (couponToSave.relatedEntity && (!couponToSave.relatedEntity.entityId || couponToSave.relatedEntity.entityId === '')) {
          // Si es un tipo que requiere relatedEntity pero el ID está vacío, mostrar error
          this.$store.dispatch('snackbar/show', {
            text: 'Para cupones de Compensación o Referido, debés especificar un ID válido',
            color: 'error'
          });
          return; // Detener el proceso de guardado
        }
        
        this.$emit('save', couponToSave)
      }
    },
    
    // Formatear fechas para mostrar en la UI
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    
    // Manejar la selección de un cliente
    handleCustomerSelected(customer) {
      if (customer && typeof customer === 'object') {
        // Si recibimos un objeto cliente completo, extraer solo el ID
        this.couponData.relatedEntity = {
          entityType: 'customer',
          entityId: customer._id
        };
      }
    },
    
    // Manejar la selección de un pedido
    handleOrderSelected(order) {
      if (order && typeof order === 'object') {
        // Si recibimos un objeto pedido completo, extraer solo el ID
        this.couponData.relatedEntity = {
          entityType: 'order',
          entityId: order._id
        };
      }
    },
    
    // Buscar clientes cuando cambia el texto de búsqueda
    async searchCustomers(query) {
      if (!query || query.length < 2) return;
      
      this.loadingCustomers = true;
      try {
        const response = await this.searchCustomersAction({ query });
        if (response && Array.isArray(response.customers)) {
          // Agregar un displayName para facilitar la visualización
          this.customers = response.customers.map(c => ({
            ...c,
            displayName: `${c.name} ${c.lastName || ''}`
          }));
        }
      } catch (error) {
        console.error('Error al buscar clientes:', error);
        this.$store.dispatch('snackbar/show', {
          text: 'Error al buscar clientes',
          color: 'error'
        });
      } finally {
        this.loadingCustomers = false;
      }
    },
    
    /**
     * Busca un pedido por su número exacto
     */
    async findOrderByNumber() {
      if (!this.orderNumberInput || this.orderNumberInput.length < 5) {
        this.orderNumberError = 'Ingresá un número de pedido válido';
        return;
      }
      
      this.orderNumberError = '';
      this.loadingOrderByNumber = true;
      
      try {
        // Usar el nuevo endpoint para buscar por número exacto
        const order = await this.findOrderByNumberAction(this.orderNumberInput.trim());
        
        if (order) {
          // Si encontramos la orden, seleccionarla
          this.selectOrder(order);
        }
      } catch (error) {
        console.error('Error al buscar pedido:', error);
        if (error.response && error.response.status === 404) {
          this.orderNumberError = `No se encontró la orden ${this.orderNumberInput}. Revisá el número exacto.`;
        } else {
          this.orderNumberError = 'Error al buscar pedido';
        }
      } finally {
        this.loadingOrderByNumber = false;
      }
    },
    
    /**
     * Selecciona un pedido y configura relatedEntity
     */
    selectOrder(order) {
      if (!order || !order._id) {
        return;
      }
      
      // Guardar el pedido seleccionado
      this.selectedOrder = order;
      this.orderSelected = true;
      
      // Asignar el ID del pedido al relatedEntity
      this.couponData.relatedEntity = {
        entityType: 'order',
        entityId: order._id
      };
      
      // Mostrar mensaje de éxito
      this.$store.dispatch('snackbar/show', {
        text: `Pedido #${order.orderNumber} seleccionado correctamente`,
        color: 'success',
        timeout: 3000
      });
    },
    
    /**
     * Limpia el pedido seleccionado
     */
    clearSelectedOrder() {
      this.selectedOrder = null;
      this.orderSelected = false;
      this.orderNumberInput = '';
      this.orderNumberError = '';
      this.couponData.relatedEntity = { entityId: '' };
    },
    
    close() {
      this.dialog = false
      this.$emit('input', false)
    }
  }
}
</script>

<style scoped>
/* Estilos adicionales si son necesarios */
</style>
